import React, { useState } from "react";
import MonthNavigator from "reusable/MonthNavigator";
import ExportRangeDialog, { ExportRange } from "reusable/ExportRangeDialog";
import { CsvCondition, csvDateTime, deviceTimeZoneExportFields } from "config/csv";
import FailedInspection from "reusable/FailedInspection";
import SortBy from "reusable/sortby";
import { useTranslation } from 'react-i18next';

interface ToolbarProps {
  searchValue: string;
  sortValue: string;
  currentPage: number;
  onSearchChange: (value: string) => void;
  onSortChange: (value: string) => void;
  onPageChange: (value: number) => void;
  /** A28（2026-11-03 和田様の UAT）。表示している月（`YYYY-MM`）。 */
  month: string;
  onMonthChange: (month: string) => void;
  onFailedInspectionChange: (value: boolean | null) => void;
  /** A28。エクスポートの範囲（この月／期間を指定／全件）の記録を全ページ読む。以前は表示中の1ページ分だけだった。 */
  fetchExportRows: (range: ExportRange) => Promise<any[]>;
}

const InspectionToolbar: React.FC<ToolbarProps> = ({
  sortValue,
  currentPage,
  onSearchChange,
  onSortChange,
  onPageChange,
  month,
  onMonthChange,
  onFailedInspectionChange,
  fetchExportRows,
}) => {
  const { t, i18n } = useTranslation('common');
  const [failedOnly, setFailedOnly] = useState<boolean | null>(null);

  const handleFailedInspectionChange = (value: boolean | null) => {
    setFailedOnly(value);
    onFailedInspectionChange(value);
  };

  const sortingOptions = [
    { label: t('sort.default'), value: "" },
    { label: t('sort.plate-number'), value: "vehicle__plate_number" },
  ];

  const handleSortChange = (value: string) => {
    onSortChange(value);
    onPageChange(1); 
  };

  // 車両点検の記録の項目。測定値・測定時刻（data / testing_time）はこの記録に無い。
  const exportFields = [
    // ADM-11-AC1。行を画面・API の記録へたどれるよう、記録の ID を先頭に出す。
    { header: 'Record ID', field: 'id' },
    // ADM-05。作成日時は日本時間で出す（UTC の ISO 文字列のままだった）。
    { header: 'Date & Time', field: 'create_time', customExport: csvDateTime },
    { header: 'Plate Number', field: 'vehicle.plate_number' },
    { header: 'Driver Name', field: 'user.name' },
    { header: 'Maintenance Manager', field: 'maintenance_manager' },
    { header: 'Operations Manager', field: 'operations_manager' },
    // EV-116 段階3。送信の時点の端末の時間帯（無い記録は空欄）。
    ...deviceTimeZoneExportFields(),
  ];
  // ADM-11-AC2。出力した条件をファイル名に入れる。A28。範囲（period）はエクスポートの画面で足す。
  const exportConditions: CsvCondition[] = [
    ['sort', sortValue],
    ['failed', failedOnly === null ? '' : String(failedOnly)],
  ];

  return (
    <section className="flex flex-row justify-between">
      <MonthNavigator month={month} onChange={onMonthChange} />
      <div className="flex flex-row justify-center items-center space-x-2">
        <SortBy
          options={sortingOptions}
          defaultValue={sortValue}
          onChange={handleSortChange}
        />
        <FailedInspection onChange={handleFailedInspectionChange} />
        <ExportRangeDialog
          month={month}
          exportFields={exportFields}
          name="vehicle_inspections"
          conditions={exportConditions}
          fetchRows={fetchExportRows}
        />
      </div>
    </section>
  );
}

export default InspectionToolbar;